import React, { useContext, useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FaPiggyBank, FaBars, FaHome, FaSignInAlt, FaUserPlus, FaSignOutAlt, FaFileInvoiceDollar, FaBuilding, FaWallet, FaChartLine, FaHandHoldingUsd } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { FaMoneyBill1Wave, FaMoneyBillTransfer } from 'react-icons/fa6';
import { FaCalendarCheck } from 'react-icons/fa';

// Backdrop for mobile overlay
const Backdrop = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: ${(props) => (props.isSidebarOpen ? 'block' : 'none')};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.55);
    z-index: 9998;
  }
`;

// Styled Sidebar
const SidebarWrapper = styled.div`
  width: ${(props) => (props.isSidebarOpen ? '250px' : '60px')};
  background-color: ${(props) => props.theme.sidebarBackground};
  transition: width 0.3s, transform 0.3s ease-in-out;
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  padding: 10px;
  display: ${(props) => (props.isSidebarVisible ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  z-index: 9999;
  overflow-x: hidden;

  @media (max-width: 768px) {
    width: 250px;
    transform: ${(props) => (props.isSidebarOpen ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.3s ease-in-out;
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 10px;

  svg {
    font-size: 1.3rem;
    cursor: pointer;
    color: ${(props) => props.theme.sidebarTextColor || '#fff'};
  }
`;

const MenuItem = styled(Link)`
  color: ${(props) => props.theme.sidebarTextColor};
  text-decoration: none;
  font-size: 1rem;
  margin: 10px 0;
  display: flex;
  align-items: center;
  padding: 10px;
  width: 100%;
  transition: background-color 0.2s;
  border-radius: 5px;
  white-space: nowrap;

  &:hover {
    background-color: #333;
  }
  svg {
    margin-right: ${(props) => (props.isSidebarOpen ? '10px' : '0')};
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    svg {
      margin-right: 10px;
    }
  }
`;

const MenuItemlogout = styled.div`
  color: ${(props) => props.theme.sidebarTextColor};
  text-decoration: none;
  font-size: 1rem;
  margin-top: auto;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px;
  transition: background-color 0.2s;
  border-radius: 5px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background-color: #333;
  }
  svg {
    margin-right: ${(props) => (props.isSidebarOpen ? '10px' : '0')};
    font-size: 1.2rem;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    svg {
      margin-right: 10px;
    }
  }
`;

// Styled Modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: grey;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  width: 90%;
  max-width: 300px;
`;

const Sidebar = ({ isSidebarOpen, onSidebarToggle }) => {
  const { isAuthenticated, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarVisible = location.pathname !== '/';
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const isMobile = () => window.innerWidth <= 768;

  // Close sidebar on route change (mobile only)
  useEffect(() => {
    if (isMobile() && isSidebarOpen) {
      onSidebarToggle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleSidebarToggle = () => {
    onSidebarToggle(!isSidebarOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = isAuthenticated
    ? [
      { to: '/dashboard', icon: <FaHome />, label: 'Dashboard' },
      { to: '/billers', icon: <FaBuilding />, label: 'Billers' },
      { to: '/bills', icon: <FaFileInvoiceDollar />, label: 'Bills' },
      { to: '/subscriptions', icon: <FaCalendarCheck />, label: 'Subscriptions' },
      { to: '/investments', icon: <FaChartLine />, label: 'Investments' },
      { to: '/receivables', icon: <FaHandHoldingUsd />, label: 'Receivables' },
      { to: '/payments', icon: <FaMoneyBillTransfer />, label: 'Payments' },
      { to: '/cash_flows', icon: <FaMoneyBill1Wave />, label: 'Cash Flows' },
      { to: '/expenses', icon: <FaWallet />, label: 'Expenses' },
      { to: '/planners', icon: <FaPiggyBank />, label: 'Planners' },
    ]
    : [
      { to: '/login', icon: <FaSignInAlt />, label: 'Login' },
      { to: '/signup', icon: <FaUserPlus />, label: 'Owner Signup' },
    ];

  return (
    <>
      {/* Mobile backdrop */}
      <Backdrop isSidebarOpen={isSidebarOpen} onClick={() => onSidebarToggle(false)} />

      <SidebarWrapper isSidebarOpen={isSidebarOpen} isSidebarVisible={isSidebarVisible}>
        <SidebarHeader>
          <FaBars onClick={handleSidebarToggle} />
        </SidebarHeader>

        {menuItems.map((item) => (
          <MenuItem key={item.to} to={item.to} isSidebarOpen={isSidebarOpen}>
            {item.icon}
            {(isSidebarOpen || isMobile()) && item.label}
          </MenuItem>
        ))}

        {isAuthenticated && (
          <MenuItemlogout onClick={() => setModalIsOpen(true)} isSidebarOpen={isSidebarOpen}>
            <FaSignOutAlt />
            {(isSidebarOpen || isMobile()) && 'Logout'}
          </MenuItemlogout>
        )}
      </SidebarWrapper>

      {/* Logout Confirmation Modal */}
      {modalIsOpen && (
        <ModalOverlay>
          <ModalContent>
            <h3>Are you sure you want to logout?</h3>
            <div style={{ marginTop: '15px' }}>
              <button
                onClick={handleLogout}
                style={{
                  background: '#e74c3c',
                  color: 'white',
                  padding: '8px 15px',
                  marginRight: '10px',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '5px',
                }}
              >
                Yes, Logout
              </button>
              <button
                onClick={() => setModalIsOpen(false)}
                style={{
                  background: '#ccc',
                  padding: '8px 15px',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '5px',
                }}
              >
                Cancel
              </button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default Sidebar;
